import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button, Card, Col, Row, Tag, Typography, Space, Modal, Form, Input,
  Select, Switch, InputNumber, Slider, message, Tooltip,
  Divider, Drawer,
} from 'antd'
import {
  PlusOutlined, DeleteOutlined, PoweroffOutlined,
  ThunderboltOutlined, SoundOutlined, BulbOutlined,
  ApiOutlined, ReloadOutlined, CloseOutlined, EyeOutlined,
} from '@ant-design/icons'
import mqtt, { type MqttClient } from 'mqtt'
import { getMqttBrokerUrl } from '../utils/mqttUrl'
import { useDeviceProfiles } from '../hooks/useDeviceProfiles'
import { useDeviceList } from '../hooks/useDevices'
import { devicesApi, type Device, type DeviceRegisterResponse } from '../api/devices'
import { devicePropertiesApi } from '../api/deviceProfiles'
import type { DeviceProfile, ServiceProperty } from '../api/deviceProfiles'

const { Title, Text } = Typography

// ── 设备类型图标映射 ──────────────────────────────────────────────

const PROFILE_ICONS: Record<string, React.ReactNode> = {
  speaker: <SoundOutlined style={{ color: '#1677ff', fontSize: 20 }} />,
  tv: <ThunderboltOutlined style={{ color: '#eb2f96', fontSize: 20 }} />,
  light: <BulbOutlined style={{ color: '#faad14', fontSize: 20 }} />,
}

const CATEGORY_LABELS: Record<string, string> = {
  actuator: '执行器',
  sensor: '传感器',
}

// ── 模拟的虚拟设备 ────────────────────────────────────────────────

interface VirtualDevice {
  device: Device
  apiKey: string
  profile: DeviceProfile
  properties: Record<string, unknown>
  isConnected: boolean
}

// ── 属性控件 ──────────────────────────────────────────────────────

function PropertyControl({
  def, value, onChange, disabled, sensorMode,
}: {
  def: ServiceProperty
  value: unknown
  onChange: (v: unknown) => void
  disabled: boolean
  sensorMode?: boolean
}) {
  const label = (
    <Text type="secondary" style={{ fontSize: 12 }}>
      {def.display_name}
      {def.unit && <span style={{ fontSize: 11 }}> ({def.unit})</span>}
    </Text>
  )

  switch (def.type) {
    case 'enum':
      return (
        <div>
          {label}
          <Select
            value={value as string}
            onChange={onChange}
            disabled={disabled || sensorMode}
            style={{ width: '100%', marginTop: 2 }}
            size="small"
            options={def.values?.map((v) => ({ value: v, label: v }))}
          />
        </div>
      )
    case 'boolean':
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {label}
          <Switch
            size="small"
            checked={value as boolean}
            onChange={(v) => onChange(v)}
            disabled={disabled || sensorMode}
          />
        </div>
      )
    case 'integer':
      if (def.min !== undefined && def.max !== undefined && def.max - def.min <= 100) {
        return (
          <div>
            {label}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
              <Slider
                value={value as number}
                onChange={(v) => onChange(v)}
                disabled={disabled || sensorMode}
                min={def.min}
                max={def.max}
                style={{ flex: 1 }}
              />
              <InputNumber
                value={value as number}
                onChange={(v) => onChange(v)}
                disabled={disabled || sensorMode}
                min={def.min}
                max={def.max}
                size="small"
                style={{ width: 60 }}
              />
            </div>
          </div>
        )
      }
      return (
        <div>
          {label}
          <InputNumber
            value={value as number}
            onChange={(v) => onChange(v)}
            disabled={disabled || sensorMode}
            min={def.min}
            max={def.max}
            size="small"
            style={{ width: '100%', marginTop: 2 }}
          />
        </div>
      )
    case 'float':
      return (
        <div>
          {label}
          <InputNumber
            value={value as number}
            onChange={(v) => onChange(v)}
            disabled={disabled || sensorMode}
            min={def.min}
            max={def.max}
            step={0.1}
            size="small"
            style={{ width: '100%', marginTop: 2 }}
          />
        </div>
      )
    default:
      return (
        <div>
          {label}
          <Input
            value={value as string}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || sensorMode}
            size="small"
            style={{ marginTop: 2 }}
          />
        </div>
      )
  }
}

// ── 传感器模拟函数 ──────────────────────────────────────────────

function generateSensorValue(def: ServiceProperty): unknown {
  switch (def.name) {
    case 'temperature':
      return +(25 + (Math.random() - 0.5) * 4).toFixed(1)
    case 'humidity':
      return +(50 + (Math.random() - 0.5) * 10).toFixed(1)
    case 'contact':
      return Math.random() > 0.5 ? 'open' : 'closed'
    case 'motion':
      return Math.random() > 0.5
    default:
      if (def.type === 'integer') {
        const min = def.min ?? 0; const max = def.max ?? 100
        return Math.floor(min + Math.random() * (max - min))
      }
      if (def.type === 'float') {
        const min = def.min ?? 0; const max = def.max ?? 100
        return +(min + Math.random() * (max - min)).toFixed(1)
      }
      if (def.type === 'boolean') return Math.random() > 0.5
      if (def.type === 'enum' && def.values?.length) {
        return def.values[Math.floor(Math.random() * def.values.length)]
      }
      return def.default
  }
}

// ── 主组件 ────────────────────────────────────────────────────────

export default function DeviceSimulator() {
  const navigate = useNavigate()
  const [devices, setDevices] = useState<VirtualDevice[]>([])
  const [loading, setLoading] = useState(false)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [createForm] = Form.useForm()
  const { data: profiles } = useDeviceProfiles()
  const { data: deviceList } = useDeviceList({ page_size: 100 })
  const devicesRef = useRef<VirtualDevice[]>([])
  const mqttClients = useRef<Map<string, MqttClient>>(new Map())

  // 同步 ref 到最新 state（React 19 禁止在 render 中操作 ref.current）
  useEffect(() => {
    devicesRef.current = devices
  }, [devices])

  // ── 详情 Drawer ────────────────────────────────────────────────
  const [drawerDevice, setDrawerDevice] = useState<VirtualDevice | null>(null)
  const [drawerIndex, setDrawerIndex] = useState(-1)

  // ── MQTT 连接管理 ─────────────────────────────────────────────

  useEffect(() => {
    const clients = mqttClients.current
    const connectedIds = new Set(devices.filter((d) => d.isConnected).map((d) => d.device.id))

    for (const [id, client] of clients) {
      if (!connectedIds.has(id)) {
        client.end(true)
        clients.delete(id)
      }
    }

    for (const vd of devices) {
      if (!vd.isConnected || clients.has(vd.device.id)) continue

      const client = mqtt.connect(getMqttBrokerUrl(), {
        clientId: `sim-${vd.device.id}-${Date.now()}`,
        clean: true,
      })

      client.on('connect', () => {
        client.subscribe(`iot/+/${vd.device.id}/commands/set`)
      })

      client.on('message', (_topic, payload) => {
        try {
          const msg = JSON.parse(payload.toString())
          const props = msg.properties || msg
          if (props && typeof props === 'object' && !Array.isArray(props)) {
            setDevices((prev) =>
              prev.map((d) =>
                d.device.id === vd.device.id
                  ? { ...d, properties: { ...d.properties, ...props } }
                  : d
              )
            )
          }
        } catch { /* ignore */ }
      })

      client.on('error', () => { /* mqtt.js handles reconnection */ })
      clients.set(vd.device.id, client)
    }

    return () => {
      for (const [, client] of clients) {
        client.end(true)
      }
      clients.clear()
    }
  }, [devices.filter((d) => d.isConnected).map((d) => d.device.id).join(',')])

  // ── 加载已有设备 ──────────────────────────────────────────────

  useEffect(() => {
    if (!profiles || !deviceList?.items?.length) return

    const existing = deviceList.items.filter(
      (d) => d.profile_id && !devicesRef.current.some((vd) => vd.device.id === d.id)
    )
    if (!existing.length) return

    for (const dev of existing) {
      const profile = profiles.find((p) => p.id === dev.profile_id)
      if (!profile) continue

      devicePropertiesApi.get(dev.id).then((res) => {
        setDevices((prev) => {
          if (prev.some((vd) => vd.device.id === dev.id)) return prev
          return [
            ...prev,
            {
              device: dev,
              apiKey: '',
              profile,
              properties: res.properties,
              isConnected: dev.status === 'online',
            },
          ]
        })
      }).catch(() => {
        const defaultProps: Record<string, unknown> = {}
        for (const svc of profile.services) {
          for (const p of svc.properties) {
            defaultProps[p.name] = p.default
          }
        }
        setDevices((prev) => {
          if (prev.some((vd) => vd.device.id === dev.id)) return prev
          return [
            ...prev,
            { device: dev, apiKey: '', profile, properties: defaultProps, isConnected: false },
          ]
        })
      })
    }
  }, [profiles, deviceList])

  // ── 定时轮询属性（MQTT 的 fallback） ──────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      const currentDevices = devicesRef.current
      if (currentDevices.length === 0) return
      for (const vd of currentDevices) {
        devicePropertiesApi.get(vd.device.id).then((res) => {
          setDevices((prev) =>
            prev.map((d) =>
              d.device.id === vd.device.id
                ? { ...d, properties: res.properties }
                : d
            )
          )
        }).catch(() => {})
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  // ── 创建设备 ──────────────────────────────────────────────────

  const handleCreate = useCallback(async (values: { name: string; profile_id: string }) => {
    setLoading(true)
    try {
      const res: DeviceRegisterResponse = await devicesApi.register({
        name: values.name,
        profile_id: values.profile_id,
      })
      const profile = profiles?.find((p) => p.id === values.profile_id)
      if (!profile) { message.error('Profile 未加载'); return }

      const defaultProps: Record<string, unknown> = {}
      for (const svc of profile.services) {
        for (const p of svc.properties) defaultProps[p.name] = p.default
      }

      setDevices((prev) => [
        ...prev,
        { device: res, apiKey: res.api_key, profile, properties: defaultProps, isConnected: false },
      ])
      setCreateModalOpen(false)
      createForm.resetFields()
      message.success(`已创建「${res.name}」`)
    } catch (err: unknown) {
      message.error((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [profiles, createForm])

  // ── 连接/断开 (JWT) ──────────────────────────────────────────

  const handleToggleConnect = useCallback(async (index: number) => {
    const vd = devicesRef.current[index]
    if (vd.isConnected) {
      setDevices((prev) => prev.map((d, i) => (i === index ? { ...d, isConnected: false } : d)))
      return
    }
    try {
      // Heartbeat 已支持 JWT 认证（Authorization header 由 axios 自动注入）
      await devicesApi.heartbeat(vd.device.id)
      setDevices((prev) => prev.map((d, i) => (i === index ? { ...d, isConnected: true } : d)))
      message.success(`${vd.device.name} 已上线`)
    } catch {
      message.error('心跳失败，请确认已登录')
    }
  }, [])

  // ── 更新属性 ────────────────────────────────────────────────

  const handlePropertyChange = useCallback(
    (index: number, name: string, value: unknown) => {
      const vd = devicesRef.current[index]
      setDevices((prev) =>
        prev.map((d, i) =>
          i === index ? { ...d, properties: { ...d.properties, [name]: value } } : d
        )
      )
      // 通过 MQTT 上报
      const client = mqttClients.current.get(vd.device.id)
      if (client?.connected) {
        client.publish(`iot/_/${vd.device.id}/properties/report`, JSON.stringify({ [name]: value }), { qos: 1 })
      }
    },
    []
  )

  // ── 传感器模拟上报 ─────────────────────────────────────────

  const handleSimulate = useCallback(async (index: number) => {
    const vd = devicesRef.current[index]
    const updates: Record<string, unknown> = {}
    for (const svc of vd.profile.services) {
      for (const p of svc.properties) updates[p.name] = generateSensorValue(p)
    }
    setDevices((prev) =>
      prev.map((d, i) => (i === index ? { ...d, properties: { ...d.properties, ...updates } } : d))
    )
    const client = mqttClients.current.get(vd.device.id)
    if (client?.connected) {
      client.publish(`iot/_/${vd.device.id}/properties/report`, JSON.stringify(updates), { qos: 1 })
      message.success(`${vd.device.name} 数据已上报`, 1)
    }
  }, [])

  // ── 删除设备 ──────────────────────────────────────────────────

  const handleDelete = useCallback(async (index: number) => {
    const vd = devicesRef.current[index]
    try {
      await devicesApi.delete(vd.device.id)
      setDevices((prev) => prev.filter((_, i) => i !== index))
      if (drawerIndex === index) {
        setDrawerDevice(null)
        setDrawerIndex(-1)
      }
      message.success(`${vd.device.name} 已删除`)
    } catch (err: unknown) {
      message.error((err as Error).message)
    }
  }, [drawerIndex])

  // ── 打开详情 Drawer ─────────────────────────────────────────

  const openDetail = useCallback((vd: VirtualDevice, index: number) => {
    setDrawerDevice(vd)
    setDrawerIndex(index)
  }, [])

  // ── 渲染精简卡片 ─────────────────────────────────────────────

  const renderDeviceCard = (vd: VirtualDevice, index: number) => {
    return (
      <Col xs={12} sm={8} lg={6} xl={4} key={vd.device.id}>
        <Card
          hoverable
          size="small"
          style={{ height: '100%', cursor: 'pointer', textAlign: 'center' }}
          onClick={() => openDetail(vd, index)}
        >
          <div style={{ padding: '4px 0' }}>
            <div style={{ marginBottom: 8 }}>
              {PROFILE_ICONS[vd.profile.id] || <ApiOutlined style={{ fontSize: 20 }} />}
            </div>
            <Text strong style={{ fontSize: 13, display: 'block' }}>{vd.device.name}</Text>
            <div style={{ marginTop: 6 }}>
              <Tag
                color={vd.isConnected ? 'green' : 'default'}
                style={{ fontSize: 10, margin: 0 }}
              >
                {vd.isConnected ? '在线' : '离线'}
              </Tag>
            </div>
            <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 4 }}>
              {vd.profile.display_name}
            </Text>
          </div>
        </Card>
      </Col>
    )
  }

  // ── 详情 Drawer 内容 ─────────────────────────────────────────

  const renderDrawerContent = () => {
    if (drawerIndex < 0 || !drawerDevice) return null
    // 使用 devices 数组中的实时数据
    const vd = devices[drawerIndex] || drawerDevice
    const idx = drawerIndex
    const isSensor = vd.profile.category === 'sensor'

    return (
      <div>
        {/* 头部信息 */}
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>
            {PROFILE_ICONS[vd.profile.id] || <ApiOutlined />}
          </div>
          <Title level={5} style={{ margin: 0 }}>{vd.device.name}</Title>
          <Space style={{ marginTop: 4 }}>
            <Tag color={vd.isConnected ? 'green' : 'default'}>
              {vd.isConnected ? '在线' : '离线'}
            </Tag>
            <Tag color={isSensor ? 'orange' : 'blue'} style={{ fontSize: 10 }}>
              {CATEGORY_LABELS[vd.profile.category] || vd.profile.category}
            </Tag>
          </Space>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>{vd.profile.display_name}</Text>
          </div>
        </div>

        {/* 状态行 */}
        <div style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {vd.isConnected ? '· MQTT 已连接' : '· MQTT 未连接'}
          </Text>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* 属性控件 */}
        <div style={{ marginBottom: 12 }}>
          {vd.profile.services.map((svc) => (
            <div key={svc.name}>
              {vd.profile.services.length > 1 && (
                <Text type="secondary" style={{ fontSize: 11 }}>{svc.description}</Text>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                {svc.properties.map((def) => (
                  <PropertyControl
                    key={def.name}
                    def={def}
                    value={vd.properties[def.name] ?? def.default}
                    onChange={(v) => handlePropertyChange(idx, def.name, v)}
                    disabled={!vd.isConnected}
                    sensorMode={isSensor}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* 操作按钮 */}
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            block
            icon={<PoweroffOutlined />}
            onClick={() => handleToggleConnect(idx)}
            type={vd.isConnected ? 'default' : 'primary'}
          >
            {vd.isConnected ? '断开' : '连接'}
          </Button>
          {isSensor && (
            <Tooltip title="模拟传感器数据上报">
              <Button
                block
                icon={<ReloadOutlined />}
                onClick={() => handleSimulate(idx)}
                disabled={!vd.isConnected}
              >
                模拟上报
              </Button>
            </Tooltip>
          )}
          <Button
            block
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(idx)}
          >
            删除
          </Button>
        </Space>
      </div>
    )
  }

  // ── 渲染 ────────────────────────────────────────────────────────

  return (
    <div>
      {/* 顶部操作栏 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Title level={4} style={{ margin: 0 }}>设备模拟器</Title>
          {devices.length > 0 && (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {devices.length} 个设备 · {devices.filter((d) => d.isConnected).length} 在线
            </Text>
          )}
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
          创建虚拟设备
        </Button>
      </div>

      {/* 空状态 */}
      {devices.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#bbb' }}>
            <ApiOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <div style={{ fontSize: 16, marginBottom: 8 }}>还没有设备</div>
            <Text type="secondary">
              点击「创建虚拟设备」或先在设备管理页创建设备
            </Text>
          </div>
        </Card>
      ) : (
        <Row gutter={[12, 12]}>
          {devices.map((vd, i) => renderDeviceCard(vd, i))}
        </Row>
      )}

      {/* 创建设备 Modal */}
      <Modal
        title="创建虚拟设备"
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); createForm.resetFields() }}
        onOk={() => createForm.submit()}
        confirmLoading={loading}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="设备名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如：模拟音箱、模拟温度计" />
          </Form.Item>
          <Form.Item name="profile_id" label="设备型号" rules={[{ required: true, message: '请选择型号' }]}>
            <Select
              placeholder="选择设备型号"
              options={profiles?.map((p) => ({
                value: p.id,
                label: (
                  <Space>
                    {PROFILE_ICONS[p.id] || <ApiOutlined />}
                    {p.display_name}
                    <Tag color={p.category === 'sensor' ? 'orange' : 'blue'} style={{ fontSize: 10 }}>
                      {CATEGORY_LABELS[p.category] || p.category}
                    </Tag>
                  </Space>
                ),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情 Drawer */}
      <Drawer
        title={drawerDevice?.device.name || '设备控制'}
        placement="right"
        width={400}
        open={!!drawerDevice}
        onClose={() => { setDrawerDevice(null); setDrawerIndex(-1) }}
        extra={
          <Space>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => drawerDevice && navigate(`/devices/${drawerDevice.device.id}`)}
            >
              详情
            </Button>
            <Button size="small" icon={<CloseOutlined />} onClick={() => { setDrawerDevice(null); setDrawerIndex(-1) }} />
          </Space>
        }
      >
        {renderDrawerContent()}
      </Drawer>
    </div>
  )
}
